import axios from 'axios'
import CryptoJS from 'crypto-js'
import baseUrl from '../../config/dev.baseUrl'
import { getLocalData, hostInfo } from '@/config/utils'
import { ensureIdentityVerificationDeviceId } from '@/config/identity-verification-entry'
import { handleGetRsaSign } from '@/config/ras-sign'

const REQUEST_TIMEOUT = 150000
const VIDEO_REQUEST_TIMEOUT = 1000000
let identityVerificationLogReporter = () => {}

/** 注入页面现有日志通道，API 层只上报脱敏后的请求阶段和结果。 */
export const setIdentityVerificationLogReporter = reporter => {
  identityVerificationLogReporter = typeof reporter === 'function' ? reporter : () => {}
}

/** 日志上报必须完全旁路业务，请求诊断异常不能阻断核身。 */
const reportIdentityVerificationHttp = (title, details) => {
  try {
    identityVerificationLogReporter(title, details)
  } catch (error) {
    // 上报失败不影响核身主流程。
  }
}

// 两个实例都绕开全局请求拦截器，防止公共参数进入统一核身请求体或视频表单。
const loggedInClient = axios.create({
  baseURL: baseUrl,
  timeout: REQUEST_TIMEOUT
})

const anonymousClient = axios.create({
  baseURL: baseUrl,
  timeout: REQUEST_TIMEOUT,
  withCredentials: true
})

const fileClient = axios.create({
  baseURL: `https://${hostInfo.apiHost.ltfile}/file`,
  timeout: VIDEO_REQUEST_TIMEOUT
})

const buildAnonymousHeaders = () => {
  try {
    return {
      'X-Identity-Device-Id': ensureIdentityVerificationDeviceId(window.localStorage, window.crypto)
    }
  } catch (error) {
    // 存储或 Web Crypto 不可用时不阻断核身，服务端仍按 code 及可选可信 IP 限流。
    return {}
  }
}

/** 登录核身只复用现有鉴权请求头，实名字段和公共签名参数不得进入请求体。 */
const buildLoggedInHeaders = () => {
  const timestamp = Date.now()
  const userInfo = getLocalData('userInfo') || {}
  const appInfo = getLocalData('appInfo')
  const token = userInfo.token || (appInfo && appInfo.token) || ''
  const lzCode = getLocalData('lzCode') === 'undefined' ? '' : (getLocalData('lzCode') || '')
  return {
    clientType: CryptoJS.MD5(`${timestamp}_web`).toString(),
    webInfo: JSON.stringify({ version: 1 }),
    timestamp,
    linktoken: getLocalData('h5_repay') ? 'h5_repay' : (appInfo ? 'app' : 'h5_lt'),
    lzCode,
    ...(token ? { token } : {}),
    ...(appInfo ? { appInfo: JSON.stringify(appInfo) } : {})
  }
}

const unwrapResponse = response => {
  const payload = response && response.data && response.data.result
    ? response.data.result
    : (response && response.data)
  if (!payload) return {}
  const code = payload.code
  if (code !== undefined && String(code) !== '1000' && String(code) !== '100000') {
    const error = new Error(payload.msg || '核身服务暂不可用')
    error.response = response
    throw error
  }
  const data = payload.data !== undefined ? payload.data : payload
  // 202 表示供应商仍处理中；匿名流程只允许 GET 查询，登录流程才保留原幂等键。
  const requestPending = response.status === 202 || Boolean(data && data.processing)
  return requestPending ? { ...data, requestPending: true } : data
}

/** 将不同网关包装收敛为页面只依赖的稳定错误码和重试信息。 */
export const normalizeIdentityVerificationError = error => {
  const status = error && error.response && error.response.status
  const responseBody = (error && error.response && error.response.data) || {}
  const payload = responseBody.result || responseBody
  const details = payload.details || payload.data || {}
  const rawCode = payload.errorCode || details.errorCode || payload.failureCode || details.failureCode || payload.code
  const statusCodeMap = {
    400: 'INVALID_REQUEST',
    401: 'INVALID_CODE',
    403: 'SOURCE_NOT_ALLOWED',
    404: 'VERIFICATION_NOT_FOUND',
    409: 'INVALID_STATE',
    410: 'EXPIRED',
    422: 'LIVENESS_FAILED',
    502: 'FACE_PROVIDER_ERROR',
    503: 'VERIFICATION_UNAVAILABLE'
  }
  const normalizedCode = rawCode && !/^\d+$/.test(String(rawCode))
    ? String(rawCode).toUpperCase()
    : (statusCodeMap[status] || 'VERIFICATION_UNAVAILABLE')
  const requestError = new Error(payload.msg || payload.message || details.message || '核身服务暂不可用')
  requestError.code = normalizedCode
  requestError.status = status || 0
  requestError.verificationStatus = details.status || payload.status || ''
  requestError.resultCode = details.resultCode || payload.resultCode || ''
  // 只有未收到 HTTP 响应时服务端处理结果未知，此时重试才必须复用原视频幂等键。
  requestError.uncertainResult = !(error && error.response)
  return requestError
}

const request = async (client, config) => {
  const startedAt = Date.now()
  reportIdentityVerificationHttp('HTTP_REQUEST', {
    stage: config.logStage || '',
    method: String(config.method || 'get').toUpperCase(),
    path: config.url || ''
  })
  try {
    const response = await client(config)
    const result = unwrapResponse(response)
    reportIdentityVerificationHttp('HTTP_SUCCESS', {
      stage: config.logStage || '',
      method: String(config.method || 'get').toUpperCase(),
      path: config.url || '',
      httpStatus: response.status || 0,
      duration: Date.now() - startedAt
    })
    return result
  } catch (error) {
    const normalizedError = normalizeIdentityVerificationError(error)
    reportIdentityVerificationHttp('HTTP_FAILED', {
      stage: config.logStage || '',
      method: String(config.method || 'get').toUpperCase(),
      path: config.url || '',
      httpStatus: normalizedError.status || 0,
      errorCode: normalizedError.code || 'VERIFICATION_UNAVAILABLE',
      duration: Date.now() - startedAt
    })
    throw normalizedError
  }
}

const withGatewaySign = payload => {
  const signedPayload = { ...payload }
  signedPayload.sign = handleGetRsaSign(signedPayload)
  return signedPayload
}

export const openLoggedInFaceRecognitionChallenge = payload => {
  // 独立 axios 不经过全局拦截器，需要显式补齐网关 V2 请求体签名；
  // 仍不注入 userName、token、envData 等公共 body 字段。
  const signedPayload = withGatewaySign(payload)
  return request(loggedInClient, {
    method: 'post',
    url: '/lt-web/xs/userAuth/faceRecognition/challenge',
    logStage: 'CHALLENGE',
    data: signedPayload,
    headers: {
      ...buildLoggedInHeaders(),
      'Content-Type': 'application/json;charset=UTF-8'
    }
  })
}

export const openAnonymousIdentityVerification = payload => request(anonymousClient, {
  method: 'post',
  url: '/lt-web/xs/anonymous/identity-verifications/open',
  logStage: 'ANONYMOUS_OPEN',
  data: payload,
  headers: {
    'Content-Type': 'application/json;charset=UTF-8',
    ...buildAnonymousHeaders()
  }
})

/** 匿名视频只允许提交一次；处理中只凭 HttpOnly 会话 cookie 与服务端解析的 IP 查询结果。 */
export const queryAnonymousIdentityVerificationResult = () => request(anonymousClient, {
  method: 'get',
  url: '/lt-web/xs/anonymous/identity-verifications',
  logStage: 'RESULT_QUERY'
})

const uploadIdentityVideo = ({ video, bizNo, randomToken }) => {
  const formData = new FormData()
  formData.append('video', video)
  formData.append('bizNo', bizNo)
  formData.append('randomNum', randomToken)
  return request(fileClient, {
    method: 'post',
    url: '/video/identity-verification',
    logStage: 'FILE_VIDEO_VALIDATE',
    data: formData,
    timeout: VIDEO_REQUEST_TIMEOUT
  })
}

export const uploadLoggedInFaceRecognitionVideo = payload => {
  return uploadIdentityVideo(payload).then(uploaded => {
    if (!uploaded || !uploaded.uploadId) throw new Error('视频处理结果无效')
    return request(loggedInClient, {
      method: 'post',
      url: '/lt-web/xs/userAuth/faceRecognition/video',
      logStage: 'LOGIN_VIDEO_SUBMIT',
      data: withGatewaySign({
        verificationId: payload.verificationId,
        uploadId: uploaded.uploadId
      }),
      headers: {
        ...buildLoggedInHeaders(),
        'Content-Type': 'application/json;charset=UTF-8',
        'Idempotency-Key': payload.idempotencyKey
      }
    })
  })
}

export const uploadAnonymousIdentityVerificationVideo = payload => {
  return uploadIdentityVideo(payload).then(uploaded => {
    if (!uploaded || !uploaded.uploadId) throw new Error('视频处理结果无效')
    return request(anonymousClient, {
      method: 'post',
      url: '/lt-web/xs/anonymous/identity-verifications/video',
      logStage: 'ANONYMOUS_VIDEO_SUBMIT',
      data: { uploadId: uploaded.uploadId },
      headers: {
        'Content-Type': 'application/json;charset=UTF-8'
      }
    })
  })
}
